import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { staffService, menusService } from '../../services';
import { Modal, Button, Loader, StaffAccessPicker } from '../../components';
import { useAuth } from '../../context/AuthContext';
import { isOwnerRole } from '../../config/menuCodeMap';

/**
 * Manage access — where one staff member can sign in.
 *
 * The same picker the Add & Edit Staff form and the Recruitment transfer use,
 * so the three axes are asked the same way wherever they are changed:
 *
 *   Carer toggle → the mobile app (the API derives the right carer role id)
 *   App access   → mobile sign-in for somebody who is not a carer
 *   Menu ticks   → the dashboard modules
 *
 * Reached from the row's ⋮ menu because changing what somebody can open is a
 * frequent, standalone job — a manager doing it through the full edit form has
 * to walk three steps of a wizard past a record they did not come to change.
 *
 * It sends ONLY those three fields. updateStaff treats an omitted key as "leave
 * it alone", so nothing else on the record can be touched from here.
 *
 * @param {object}   staff    the row this was opened from ({ id, name })
 * @param {Function} onSaved  called after a successful save
 */
const ManageAccessModal = ({ isOpen, onClose, staff, onSaved }) => {
  const { user, sites, activeSiteId } = useAuth();
  // Only the account owner sees, and can flip, the Manager switch.
  const isOwner = isOwnerRole(user);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);

  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  // The module list is the organisation's, not this person's, so it is fetched
  // once rather than on every open.
  useEffect(() => {
    if (!isOpen) return;
    menusService.getUserMenus()
      .then((response) => {
        if (response?.success) {
          setAvailableMenus((response.data.allMenus || []).filter((menu) => !menu.is_admin));
        }
      })
      .catch(() => { /* the picker says so when the list is unavailable */ })
      .finally(() => setLoadingMenus(false));
  }, [isOpen]);

  // Read what they have now, every time. Their access may have been changed
  // from the edit form since this list was loaded, and saving a stale copy back
  // would quietly revoke whatever was granted in between.
  useEffect(() => {
    if (!isOpen || !staff?.id) return;
    let cancelled = false;

    setForm(null);
    setLoading(true);

    staffService.getStaffById(staff.id)
      .then((response) => {
        if (cancelled) return;
        const record = response?.data || {};
        setForm({
          is_carer: record.is_carer === true,
          app_access: record.app_access === true,
          medication_access: record.medication_access === true,
          menu_ids: (record.menu_ids || []).map(Number),
          is_manager: record.is_manager === true,
          site_ids: Array.isArray(record.site_ids) ? record.site_ids.map(Number) : [],
        });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Could not load their current access');
        onClose();
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, staff?.id]);

  const submit = async () => {
    // Either axis, or both, but never neither — somebody with neither has
    // nowhere to sign in. The API refuses it too; this only saves a round trip
    // and says so in the same words.
    if (!form.is_manager && !form.is_carer && !form.app_access && form.menu_ids.length === 0) {
      toast.error('Give them the mobile app, at least one dashboard module, or both.');
      return;
    }
    try {
      setSaving(true);
      const response = await staffService.updateStaff(staff.id, {
        is_carer: form.is_carer,
        app_access: form.app_access,
        medication_access: form.is_carer && form.medication_access,
        menu_ids: form.menu_ids,
        ...(isOwner && {
          is_manager: form.is_manager,
          ...(form.is_manager && { site_ids: form.site_ids?.length ? form.site_ids : [activeSiteId] }),
        }),
      });
      if (response?.success === false) {
        toast.error(response.message || 'Could not save their access');
        return;
      }
      toast.success(`Access updated for ${staff.name}`);
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Could not save their access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Access: ${staff?.name || ''}`}
      maxWidth="max-w-2xl"
      footer={(
        <div className="flex justify-end gap-2.5">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!form} onClick={submit}>
            Save access
          </Button>
        </div>
      )}
    >
      {loading || !form ? (
        <div className="py-12"><Loader /></div>
      ) : (
        <StaffAccessPicker
          isCarer={form.is_carer}
          appAccess={form.app_access}
          medicationAccess={form.medication_access}
          menuIds={form.menu_ids}
          availableMenus={availableMenus}
          loadingMenus={loadingMenus}
          onChange={patch}
          canMakeManager={isOwner}
          isManager={form.is_manager}
          sites={sites}
          siteIds={form.site_ids?.length ? form.site_ids : [activeSiteId].filter(Boolean)}
        />
      )}
    </Modal>
  );
};

export default ManageAccessModal;
